import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Check, Compass, Building2, Warehouse, Users2 } from "lucide-react";
import { useScope } from "@/hooks/use-scope";
import { useImplementationProgress } from "@/lib/implementation-progress";
import { TrackPageHeader, ChecklistStrip } from "@/components/implementation/track-page";

export const Route = createFileRoute("/_authenticated/app/implementation/workspace/foundation")({
  component: FoundationTrack,
});

type Move = {
  id: string;
  step: string;
  milestone: string;
  purpose: string;
  consequence: string;
  Icon: typeof Compass;
  primary: { label: string; to: string };
  secondary?: { label: string; to: string };
  evidence: { label: string; value: string; done: boolean }[];
  ready: boolean;
};

function FoundationTrack() {
  const { tenantId } = useScope();
  const progress = useImplementationProgress(tenantId);
  const track = progress.tracks.foundation;
  const ev = progress.evidence;

  const blueprintReady = !!ev.blueprint;
  const facilityReady =
    !!ev.primaryFacility && ev.primaryFacility.hasAddress && !!ev.primaryFacility.timezone;
  const teamReady = ev.teamCount > 1;
  const companyReady = !!ev.tenantName;

  const moves: Move[] = [
    {
      id: "blueprint",
      step: "Move 01",
      milestone: "Set the operating model",
      purpose:
        "Your Blueprint tells CairnOS what kind of operation this is. It's the single decision every subsequent recommendation is derived from — the policies we suggest, the integrations we surface, the modes your workers see on the floor.",
      consequence:
        "Change this later and CairnOS will re-recommend without breaking what you've already committed. Nothing here is locked.",
      Icon: Compass,
      primary: {
        label: blueprintReady ? "Review operating model" : "Choose operating model",
        to: "/app/admin/blueprints",
      },
      evidence: [
        {
          label: "Blueprint",
          value: ev.blueprint ?? "Not yet selected",
          done: blueprintReady,
        },
      ],
      ready: blueprintReady,
    },
    {
      id: "company",
      step: "Move 02",
      milestone: "Establish the operating entity",
      purpose:
        "The legal identity that appears on BOLs, invoices, packing slips, and the merchant portals your customers will log in to. This is who the operation runs as.",
      consequence:
        "Everything CairnOS emits — documents, notifications, portal branding — inherits from this identity.",
      Icon: Building2,
      primary: { label: "Open company profile", to: "/app/admin/tenants" },
      evidence: [
        {
          label: "Legal name",
          value: ev.tenantName ?? "Not yet named",
          done: companyReady,
        },
      ],
      ready: companyReady,
    },
    {
      id: "facility",
      step: "Move 03",
      milestone: "Commission your first facility",
      purpose:
        "The physical building CairnOS will run. Address, timezone, and operating hours become the ground truth for order cutoffs, carrier pickup windows, wave planning, and every SLA the system defends.",
      consequence:
        "Additional facilities are added later from Operations Workspace — the first one anchors every downstream decision.",
      Icon: Warehouse,
      primary: {
        label: facilityReady ? "Review facility" : "Configure facility",
        to: "/app/admin/facilities",
      },
      secondary:
        ev.facilityCount > 1
          ? { label: `${ev.facilityCount} facilities`, to: "/app/admin/facilities" }
          : undefined,
      evidence: [
        {
          label: "Facility",
          value: ev.primaryFacility?.name ?? "Not yet commissioned",
          done: !!ev.primaryFacility,
        },
        {
          label: "Location",
          value: ev.primaryFacility?.city
            ? `${ev.primaryFacility.city}${
                ev.primaryFacility.region ? ", " + ev.primaryFacility.region : ""
              }`
            : "Address pending",
          done: !!ev.primaryFacility?.hasAddress,
        },
        {
          label: "Timezone",
          value: ev.primaryFacility?.timezone ?? "Not set",
          done: !!ev.primaryFacility?.timezone,
        },
      ],
      ready: facilityReady,
    },
    {
      id: "team",
      step: "Move 04",
      milestone: "Seat your operating team",
      purpose:
        "The people who will implement alongside you and, from Day 1, run the floor. Roles determine what workspace each teammate lands in — Worker, Operations, Admin — and what actions they can take.",
      consequence:
        "You can adjust roles at any time from Admin. The team you seat here is the one CairnOS will assign work to once you go live.",
      Icon: Users2,
      primary: {
        label: teamReady ? "Manage team" : "Invite your team",
        to: "/app/admin/users",
      },
      evidence: [
        {
          label: "Team members",
          value:
            ev.teamCount === 0
              ? "None yet"
              : ev.teamCount === 1
                ? "Just you"
                : `${ev.teamCount} seated`,
          done: teamReady,
        },
      ],
      ready: teamReady,
    },
  ];

  const readyCount = moves.filter((m) => m.ready).length;
  const foundationReady = readyCount === moves.length;

  return (
    <div>
      <TrackPageHeader
        track={track}
        eyebrow="Track 01 · Foundation"
        description="The infrastructure your warehouse runs on. Four operating moves. Everything after Foundation is derived from them."
      />

      {/* Readiness banner */}
      <div className="mb-6 rounded-xl border border-border bg-card px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Foundation readiness
            </div>
            <div className="mt-1 text-sm text-foreground">
              {foundationReady ? (
                <>
                  Foundation established.{" "}
                  <span className="text-muted-foreground">
                    You&rsquo;re ready to load operating data.
                  </span>
                </>
              ) : (
                <>
                  {readyCount} of {moves.length} operating moves complete.{" "}
                  <span className="text-muted-foreground">
                    Each move anchors a downstream decision.
                  </span>
                </>
              )}
            </div>
          </div>
          {foundationReady ? (
            <Link
              to="/app/implementation/workspace/data"
              className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-xs font-medium text-primary-foreground hover:bg-primary/90"
            >
              Continue to Data track
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Link>
          ) : null}
        </div>
      </div>

      <ChecklistStrip track={track} />

      {/* Commissioning moves */}
      <ol className="mt-2 space-y-3">
        {moves.map((m) => (
          <MoveRow key={m.id} move={m} />
        ))}
      </ol>

      <UnlocksPanel />
    </div>
  );
}

function MoveRow({ move }: { move: Move }) {
  const { Icon } = move;
  return (
    <li className="group rounded-xl border border-border bg-card transition-colors hover:border-primary/40">
      <div className="grid gap-5 p-5 sm:grid-cols-[auto,1fr,auto] sm:items-start">
        {/* Step marker */}
        <div className="flex items-start gap-3">
          <div
            className={
              "grid h-11 w-11 shrink-0 place-items-center rounded-lg border " +
              (move.ready
                ? "border-primary/30 bg-primary/10 text-primary"
                : "border-border bg-muted/50 text-muted-foreground")
            }
          >
            {move.ready ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
          </div>
          <div className="min-w-0 sm:hidden">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              {move.step}
            </div>
            <div className="mt-0.5 text-base font-semibold text-foreground">{move.milestone}</div>
          </div>
        </div>

        {/* Body */}
        <div className="min-w-0">
          <div className="hidden sm:block">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              {move.step}
            </div>
            <div className="mt-0.5 text-base font-semibold tracking-tight text-foreground">
              {move.milestone}
            </div>
          </div>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            {move.purpose}
          </p>
          <p className="mt-2 max-w-2xl text-xs leading-relaxed text-muted-foreground/80">
            {move.consequence}
          </p>

          {/* Evidence */}
          <dl className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
            {move.evidence.map((e) => (
              <div key={e.label} className="min-w-0">
                <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {e.label}
                </dt>
                <dd
                  className={
                    "mt-0.5 truncate font-mono text-[12px] " +
                    (e.done ? "text-foreground" : "text-muted-foreground/70")
                  }
                >
                  {e.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Actions */}
        <div className="flex flex-col items-stretch gap-2 sm:min-w-[180px]">
          <Link
            to={move.primary.to}
            className="inline-flex h-9 items-center justify-center rounded-md bg-foreground px-4 text-xs font-medium text-background hover:bg-foreground/90"
          >
            {move.primary.label}
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Link>
          {move.secondary ? (
            <Link
              to={move.secondary.to}
              className="inline-flex h-8 items-center justify-center rounded-md border border-border bg-background px-3 text-[11px] font-medium text-muted-foreground hover:text-foreground"
            >
              {move.secondary.label}
            </Link>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function UnlocksPanel() {
  return (
    <div className="mt-6 rounded-xl border border-dashed border-border bg-muted/30 p-5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        What Foundation unlocks
      </div>
      <div className="mt-3 grid gap-4 sm:grid-cols-3">
        <UnlockItem
          title="Data intake"
          body="Customers, SKUs, locations, and opening inventory can be loaded once your facility exists to hold them."
        />
        <UnlockItem
          title="Operating policies"
          body="CairnOS begins recommending policies based on your Blueprint — putaway, allocation, replenishment, returns."
        />
        <UnlockItem
          title="Team assignment"
          body="Once teammates are seated, work can be routed to specific operators from Day 1."
        />
      </div>
    </div>
  );
}

function UnlockItem({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <div className="text-sm font-semibold text-foreground">{title}</div>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}